import React, { useState, useEffect } from 'react';
import { auth, db } from '../firebase';
import {
  collection,
  doc,
  addDoc,
  getDocs,
  deleteDoc,
  serverTimestamp,
} from 'firebase/firestore';
import { useNavigate } from 'react-router-dom';
import './AddRecipe.css';
import PageHeader from './PageHeader';

function AddRecipe() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });

  // États pour les données de la recette
  const [recipeName, setRecipeName] = useState('');
  const [description, setDescription] = useState('');
  const [prepTime, setPrepTime] = useState('');
  const [cookTime, setCookTime] = useState('');
  const [servings, setServings] = useState('');
  const [difficulty, setDifficulty] = useState('');
  const [source, setSource] = useState('');

  // États pour les catégories
  const [categories, setCategories] = useState([]);
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [showNewCategoryForm, setShowNewCategoryForm] = useState(false);
  const [newCategoryName, setNewCategoryName] = useState('');
  const [newCategoryColor, setNewCategoryColor] = useState('');

  // États pour le sélecteur de catégories amélioré
  const [categorySearchTerm, setCategorySearchTerm] = useState('');
  const [showCategoryDropdown, setShowCategoryDropdown] = useState(false);
  const [filteredCategories, setFilteredCategories] = useState([]);

  // États pour les ingrédients
  const [ingredients, setIngredients] = useState([
    { id: 1, name: '', quantity: '', unit: '', comment: '' },
  ]);

  // États pour les étapes
  const [steps, setSteps] = useState([
    { id: 1, description: '', timeEstimate: '' },
  ]);

  // Fonction pour ajuster automatiquement la hauteur des textareas
  const adjustTextareaHeight = element => {
    if (!element) return;

    // Réinitialiser la hauteur pour obtenir la bonne hauteur de scrollHeight
    element.style.height = 'auto';

    // Définir la hauteur en fonction du contenu
    element.style.height = `${element.scrollHeight}px`;
  };

  // Effet pour ajuster les textareas existants lors du rendu initial
  useEffect(() => {
    const textareas = document.querySelectorAll('textarea');
    textareas.forEach(textarea => {
      adjustTextareaHeight(textarea);
    });
  }, [ingredients, steps]); // S'exécute lorsque ingredients ou steps changent

  // Effet pour filtrer les catégories en fonction de la recherche
  useEffect(() => {
    if (categorySearchTerm.trim() === '') {
      setFilteredCategories(categories);
    } else {
      const filtered = categories.filter(category =>
        category.nom.toLowerCase().includes(categorySearchTerm.toLowerCase())
      );
      setFilteredCategories(filtered);
    }
  }, [categorySearchTerm, categories]);

  // Vérifier l'authentification et charger les données utilisateur
  useEffect(() => {
    const checkAuth = async () => {
      if (auth.currentUser) {
        await loadCategories();
        setLoading(false);
      } else {
        // Rediriger vers la page de connexion si non authentifié
        navigate('/');
      }
    };

    checkAuth();
  }, [navigate]);

  // Fermer le dropdown lorsque l'utilisateur clique à l'extérieur
  useEffect(() => {
    const handleClickOutside = event => {
      if (
        showCategoryDropdown &&
        !event.target.closest('.category-input-container') &&
        !event.target.closest('.new-category-form')
      ) {
        setShowCategoryDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showCategoryDropdown]);

  // Charger les catégories de l'utilisateur
  const loadCategories = async () => {
    try {
      if (!auth.currentUser) return;

      const categoriesCollection = collection(
        db,
        'categoriesUtilisateur',
        auth.currentUser.uid,
        'categories'
      );

      const querySnapshot = await getDocs(categoriesCollection);
      const categoriesList = [];

      querySnapshot.forEach(doc => {
        categoriesList.push({
          id: doc.id,
          ...doc.data(),
        });
      });

      // Trier par ordre si disponible
      categoriesList.sort((a, b) => (a.ordre || 0) - (b.ordre || 0));
      setCategories(categoriesList);
      setFilteredCategories(categoriesList);
    } catch (error) {
      console.error('Erreur lors du chargement des catégories:', error);
      setMessage({
        type: 'error',
        text: 'Impossible de charger vos catégories. Veuillez réessayer.',
      });
    }
  };

  // Gérer l'ajout d'un nouvel ingrédient
  const handleAddIngredient = () => {
    const newId =
      ingredients.length > 0
        ? Math.max(...ingredients.map(ing => ing.id)) + 1
        : 1;

    setIngredients([
      ...ingredients,
      { id: newId, name: '', quantity: '', unit: '', comment: '' },
    ]);
  };

  // Gérer la suppression d'un ingrédient
  const handleRemoveIngredient = id => {
    if (ingredients.length <= 1) return;
    setIngredients(ingredients.filter(ing => ing.id !== id));
  };

  // Gérer la modification d'un ingrédient
  const handleIngredientChange = (id, field, value) => {
    setIngredients(
      ingredients.map(ing => (ing.id === id ? { ...ing, [field]: value } : ing))
    );
  };

  // Gérer l'ajout d'une nouvelle étape
  const handleAddStep = () => {
    const newId =
      steps.length > 0 ? Math.max(...steps.map(step => step.id)) + 1 : 1;

    setSteps([...steps, { id: newId, description: '', timeEstimate: '' }]);
  };

  // Gérer la suppression d'une étape
  const handleRemoveStep = id => {
    if (steps.length <= 1) return;
    setSteps(steps.filter(step => step.id !== id));
  };

  // Gérer la modification d'une étape
  const handleStepChange = (id, field, value) => {
    setSteps(
      steps.map(step => (step.id === id ? { ...step, [field]: value } : step))
    );
  };

  // Basculer la sélection d'une catégorie
  const toggleCategory = categoryId => {
    if (selectedCategories.includes(categoryId)) {
      setSelectedCategories(selectedCategories.filter(id => id !== categoryId));
    } else {
      setSelectedCategories([...selectedCategories, categoryId]);
    }
  };

  // Supprimer une catégorie
  const handleDeleteCategory = async (categoryId, e) => {
    e.stopPropagation();
    try {
      await deleteDoc(doc(db, 'categoriesUtilisateur', auth.currentUser.uid, 'categories', categoryId));
      setCategories(prev => prev.filter(c => c.id !== categoryId));
      setSelectedCategories(prev => prev.filter(id => id !== categoryId));
    } catch (err) {
      setMessage({ type: 'error', text: 'Erreur lors de la suppression de la catégorie.' });
    }
  };

  // Ajouter une nouvelle catégorie
  const handleAddCategory = async () => {
    if (!newCategoryName.trim()) {
      setMessage({
        type: 'error',
        text: 'Veuillez entrer un nom pour la catégorie',
      });
      return;
    }

    if (!newCategoryColor) {
      setMessage({
        type: 'error',
        text: 'Veuillez choisir une couleur pour la catégorie',
      });
      return;
    }

    try {
      // Créer la référence à la collection des catégories de l'utilisateur
      const categoriesCollection = collection(
        db,
        'categoriesUtilisateur',
        auth.currentUser.uid,
        'categories'
      );

      // Déterminer l'ordre de la nouvelle catégorie
      const newOrder =
        categories.length > 0
          ? Math.max(...categories.map(cat => cat.ordre || 0)) + 1
          : 0;

      // Ajouter la nouvelle catégorie
      const newCategoryData = {
        nom: newCategoryName.trim(),
        couleur: newCategoryColor,
        dateCreation: serverTimestamp(),
        ordre: newOrder,
      };

      const newCategoryRef = await addDoc(
        categoriesCollection,
        newCategoryData
      );

      // Mettre à jour l'état local
      const newCategory = {
        id: newCategoryRef.id,
        ...newCategoryData,
      };

      setCategories([...categories, newCategory]);
      setSelectedCategories([...selectedCategories, newCategoryRef.id]);

      // Réinitialiser le formulaire
      setNewCategoryName('');
      setNewCategoryColor('');
      setShowNewCategoryForm(false);
      setShowCategoryDropdown(false);
      setCategorySearchTerm('');

      setMessage({
        type: 'success',
        text: 'Catégorie ajoutée avec succès',
      });

      // Effacer le message après quelques secondes
      setTimeout(() => setMessage({ type: '', text: '' }), 3000);
    } catch (error) {
      console.error("Erreur lors de l'ajout de la catégorie:", error);
      setMessage({
        type: 'error',
        text: "Erreur lors de l'ajout de la catégorie. Veuillez réessayer.",
      });
    }
  };

  // Valider le formulaire avant soumission
  const validateForm = () => {
    if (!recipeName.trim()) {
      setMessage({
        type: 'error',
        text: 'Veuillez entrer un nom pour la recette',
      });
      return false;
    }

    // Vérifier que le nombre de personnes est valide si renseigné
    if (servings && (isNaN(servings) || parseInt(servings) < 0)) {
      setMessage({
        type: 'error',
        text: 'Le nombre de personnes doit être un nombre positif',
      });
      return false;
    }

    // Vérifier que le temps de préparation est valide si renseigné
    if (prepTime && (isNaN(prepTime) || parseInt(prepTime) < 0)) {
      setMessage({
        type: 'error',
        text: 'Le temps de préparation doit être un nombre positif',
      });
      return false;
    }

    // La difficulté n'est plus obligatoire

    // Vérifier que tous les ingrédients ont au moins un nom
    const hasEmptyIngredient = ingredients.some(ing => !ing.name.trim());
    if (hasEmptyIngredient) {
      setMessage({
        type: 'error',
        text: 'Tous les ingrédients doivent avoir un nom',
      });
      return false;
    }

    // La vérification que toutes les étapes ont une description a été retirée
    // La description des étapes est maintenant facultative

    return true;
  };

  // Sauvegarder la recette
  const saveRecipe = async () => {
    if (!validateForm()) return;

    setSaving(true);
    setMessage({ type: '', text: '' });

    try {
      // Préparer les données de la recette
      const recipeData = {
        nom: recipeName.trim(),
        description: description.trim(),
        tempsPreparation: prepTime ? parseInt(prepTime) : 0,
        tempsCuisson: cookTime ? parseInt(cookTime) : 0,
        nombrePersonnes: servings ? parseInt(servings) : 0,
        difficulte: difficulty || null,
        categories: selectedCategories,
        ingredients: ingredients.map(ing => ({
          nom: ing.name.trim(),
          quantite: ing.quantity.trim() || null,
          unite: ing.unit.trim(),
          commentaire: ing.comment.trim(),
        })),
        etapesPreparation: steps.map((step, index) => ({
          numero: index + 1,
          description: step.description.trim(),
        })),
        urlImage: '', // À implémenter plus tard
        tags: [], // À implémenter plus tard
        source: source.trim(),
        dateCreation: serverTimestamp(),
        dateMiseAJour: serverTimestamp(),
      };

      // Créer la référence à la collection des recettes de l'utilisateur
      const recettesCollection = collection(
        db,
        'recettesUtilisateur',
        auth.currentUser.uid,
        'recettes'
      );

      // Ajouter la recette
      const newRecipeRef = await addDoc(recettesCollection, recipeData);

      setMessage({
        type: 'success',
        text: 'Recette enregistrée avec succès!',
      });

      // Rediriger vers la page de la recette nouvellement créée après 1 seconde
      setTimeout(() => {
        navigate(`/recipe/${newRecipeRef.id}`);
      }, 1000);
    } catch (error) {
      console.error("Erreur lors de l'enregistrement de la recette:", error);
      setMessage({
        type: 'error',
        text:
          "Erreur lors de l'enregistrement. Veuillez réessayer. " +
          error.message,
      });
    } finally {
      setSaving(false);
    }
  };

  // Retourner à la liste des recettes
  const handleCancel = () => {
    navigate('/recipes');
  };

  // Icône de poubelle SVG
  const TrashIcon = () => (
    <svg
      className="trash-icon"
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M3 6h18"></path>
      <path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6"></path>
      <path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2"></path>
    </svg>
  );

  if (loading) {
    return <div className="loading">Chargement...</div>;
  }

  return (
    <div className="add-recipe-container">
      <PageHeader
        title="Ajouter une nouvelle recette"
        showBackButton={false}
        sticky={true}
        actions={
          <>
            <button className="cancel-button" onClick={handleCancel}>
              Annuler
            </button>
            <button
              className="save-button"
              onClick={saveRecipe}
              disabled={saving}
            >
              {saving ? 'Enregistrement...' : 'Enregistrer'}
            </button>
          </>
        }
      />

      {message.text && (
        <div className={`message ${message.type}`}>{message.text}</div>
      )}

      {/* Section Informations générales */}
      <div className="form-section">
        <div className="form-section-header">
          <h2 className="form-section-title">Informations générales</h2>
        </div>

        {/* Sélecteur de catégories amélioré */}
        <div className="category-selector">
          <label
            style={{ display: 'block', marginBottom: '6px', fontWeight: '500' }}
          >
            Catégories
          </label>

          <div className="category-input-container">
            <div
              className="category-input-wrapper"
              onClick={() => setShowCategoryDropdown(true)}
            >
              {/* Catégories sélectionnées sous forme de chips */}
              {selectedCategories.map(catId => {
                const category = categories.find(c => c.id === catId);
                return category ? (
                  <div
                    key={category.id}
                    className="category-chip"
                    style={{ backgroundColor: category.couleur }}
                  >
                    {category.nom}
                    <button
                      className="remove-category"
                      onClick={e => {
                        e.stopPropagation();
                        toggleCategory(category.id);
                      }}
                    >
                      ×
                    </button>
                  </div>
                ) : null;
              })}

              {/* Champ de recherche */}
              <input
                type="text"
                className="category-input"
                placeholder="Rechercher ou ajouter une catégorie"
                value={categorySearchTerm}
                onChange={e => setCategorySearchTerm(e.target.value)}
                onFocus={() => setShowCategoryDropdown(true)}
              />
            </div>

            {/* Dropdown des résultats */}
            {showCategoryDropdown && (
              <div className="category-dropdown">
                {/* Bouton créer toujours en haut */}
                <div
                  className="add-new-category"
                  onClick={() => {
                    setNewCategoryName(categorySearchTerm);
                    setShowNewCategoryForm(true);
                    setShowCategoryDropdown(false);
                  }}
                >
                  <span>+</span> Nouvelle catégorie
                </div>

                {filteredCategories.length > 0 ? (
                  <div className="category-list">
                    {filteredCategories.map(category => (
                      <div
                        key={category.id}
                        className={`category-option ${
                          selectedCategories.includes(category.id) ? 'active' : ''
                        }`}
                        onClick={() => toggleCategory(category.id)}
                        style={
                          selectedCategories.includes(category.id)
                            ? { backgroundColor: category.couleur }
                            : {}
                        }
                      >
                        <div className="color-dot" style={{ backgroundColor: category.couleur }}></div>
                        <span>{category.nom}</span>
                        {selectedCategories.includes(category.id) && (
                          <span className="check">✓</span>
                        )}
                        <button
                          className="delete-category-btn"
                          onClick={e => handleDeleteCategory(category.id, e)}
                          title="Supprimer cette catégorie"
                        >
                          <svg viewBox="0 0 24 24" fill="currentColor" width="14" height="14">
                            <path d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z"/>
                          </svg>
                        </button>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="no-results">
                    <span>Aucune catégorie trouvée{categorySearchTerm ? ` pour "${categorySearchTerm}"` : ''}</span>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {showNewCategoryForm && (
          <div className="new-category-form">
            <div className="category-creator">
              <div className="new-category-name-field">
                <label className="new-category-label">Nom de la catégorie</label>
                <input
                  type="text"
                  value={newCategoryName}
                  onChange={e => setNewCategoryName(e.target.value)}
                  placeholder="Ex : Desserts, Végétarien..."
                  autoFocus
                  className="new-category-name-input"
                  onKeyDown={e => e.key === 'Enter' && handleAddCategory()}
                />
              </div>

              {newCategoryColor && newCategoryName && (
                <div className="category-preview-container">
                  <div
                    className="category-preview has-color"
                    style={{ backgroundColor: newCategoryColor }}
                  >
                    <span>{newCategoryName}</span>
                  </div>
                </div>
              )}

              <div className="color-selector">
                <p>Choisissez une couleur pour votre catégorie :</p>
                <div className="color-options">
                  {[
                    '#FF5252', // Rouge
                    '#FF7043', // Orange
                    '#8D6E63', // Marron
                    '#5C6BC0', // Bleu
                    '#7C4DFF', // Violet
                    '#009688', // Turquoise
                    '#2E7D32', // Vert foncé
                    '#C2185B', // Rose foncé
                  ].map((color, index) => (
                    <button
                      key={index}
                      className={`color-option ${
                        newCategoryColor === color ? 'selected' : ''
                      }`}
                      style={{ backgroundColor: color }}
                      onClick={() => setNewCategoryColor(color)}
                      aria-label={`Couleur ${index + 1}`}
                    />
                  ))}
                </div>
              </div>
            </div>

            <div className="button-group">
              <button
                className="action-button"
                onClick={handleAddCategory}
                disabled={!newCategoryName.trim() || !newCategoryColor}
              >
                Ajouter la catégorie
              </button>
              <button
                className="cancel-button small"
                onClick={() => setShowNewCategoryForm(false)}
              >
                Annuler
              </button>
            </div>
          </div>
        )}

        <div className="input-group">
          <label>Nom de la recette *</label>
          <input
            type="text"
            value={recipeName}
            onChange={e => setRecipeName(e.target.value)}
            placeholder="Ex: Tarte aux pommes"
            required
          />
        </div>

        <div className="input-group">
          <label>Description</label>
          <textarea
            value={description}
            onChange={e => {
              setDescription(e.target.value);
              adjustTextareaHeight(e.target);
            }}
            placeholder="Une description rapide de la recette"
            rows="3"
          />
        </div>

        <div className="input-row">
          <div className="input-group">
            <label>Temps de préparation (minutes)</label>
            <input
              type="number"
              value={prepTime}
              onChange={e => setPrepTime(e.target.value)}
              min="0"
              placeholder="Ex: 20"
            />
          </div>

          <div className="input-group">
            <label>Temps de cuisson (minutes)</label>
            <input
              type="number"
              value={cookTime}
              onChange={e => setCookTime(e.target.value)}
              min="0"
              placeholder="Ex: 30"
            />
          </div>

          <div className="input-group">
            <label>Nombre de personnes</label>
            <input
              type="number"
              value={servings}
              onChange={e => setServings(e.target.value)}
              min="0"
              placeholder="Ex: 4"
            />
          </div>
        </div>

        <div className="input-group">
          <label>Difficulté</label>
          <div className="difficulty-selector">
            <button
              type="button"
              className={difficulty === 'Facile' ? 'selected' : ''}
              onClick={() => setDifficulty('Facile')}
            >
              Facile
            </button>
            <button
              type="button"
              className={difficulty === 'Moyen' ? 'selected' : ''}
              onClick={() => setDifficulty('Moyen')}
            >
              Moyen
            </button>
            <button
              type="button"
              className={difficulty === 'Difficile' ? 'selected' : ''}
              onClick={() => setDifficulty('Difficile')}
            >
              Difficile
            </button>
          </div>
        </div>

        <div className="input-group">
          <label>Source / Origine</label>
          <input
            type="text"
            value={source}
            onChange={e => setSource(e.target.value)}
            placeholder="Ex: Livre de cuisine, site web, tradition familiale..."
          />
        </div>
      </div>

      {/* Section Ingrédients */}
      <div className="form-section">
        <div className="form-section-header">
          <h2 className="form-section-title">Ingrédients</h2>
        </div>

        {ingredients.map(ingredient => (
          <div key={ingredient.id} className="ingredient-row">
            <button
              type="button"
              className="remove-button"
              onClick={() => handleRemoveIngredient(ingredient.id)}
              disabled={ingredients.length <= 1}
              aria-label="Supprimer l'ingrédient"
            >
              <TrashIcon />
            </button>
            <div className="ingredient-header">
              <div className="input-row">
                <div className="input-group ingredient-quantity">
                  <label>Quantité</label>
                  <input
                    type="text"
                    value={ingredient.quantity}
                    onChange={e =>
                      handleIngredientChange(
                        ingredient.id,
                        'quantity',
                        e.target.value
                      )
                    }
                    placeholder="200, 1 filet..."
                  />
                </div>
                <div className="input-group ingredient-unit">
                  <label>Unité</label>
                  <input
                    type="text"
                    value={ingredient.unit}
                    onChange={e =>
                      handleIngredientChange(
                        ingredient.id,
                        'unit',
                        e.target.value
                      )
                    }
                    placeholder="Ex: g"
                  />
                </div>
                <div className="input-group ingredient-name">
                  <label>Nom de l'ingrédient *</label>
                  <input
                    type="text"
                    value={ingredient.name}
                    onChange={e =>
                      handleIngredientChange(
                        ingredient.id,
                        'name',
                        e.target.value
                      )
                    }
                    placeholder="Ex: Farine"
                    required
                  />
                </div>
              </div>
            </div>
            <div className="input-group ingredient-comment">
              <label>Commentaire (optionnel)</label>
              <input
                type="text"
                value={ingredient.comment}
                onChange={e =>
                  handleIngredientChange(
                    ingredient.id,
                    'comment',
                    e.target.value
                  )
                }
                placeholder="Ex: tamisée, à température ambiante..."
              />
            </div>
          </div>
        ))}

        <button
          type="button"
          className="add-button"
          onClick={handleAddIngredient}
        >
          + Ajouter un ingrédient
        </button>
      </div>

      {/* Section Étapes de préparation */}
      <div className="form-section">
        <div className="form-section-header">
          <h2 className="form-section-title">Étapes de préparation</h2>
        </div>

        {steps.map((step, index) => (
          <div key={step.id} className="step-row">
            <button
              type="button"
              className="remove-button"
              onClick={() => handleRemoveStep(step.id)}
              disabled={steps.length <= 1}
              aria-label="Supprimer l'étape"
            >
              <TrashIcon />
            </button>
            <div className="step-number">{index + 1}</div>
            <div className="step-content">
              <div className="input-group">
                <label>Description de l'étape</label>
                <textarea
                  value={step.description}
                  onChange={e => {
                    handleStepChange(step.id, 'description', e.target.value);
                    adjustTextareaHeight(e.target);
                  }}
                  placeholder="Ex: Mélanger les ingrédients secs dans un bol..."
                  rows="2"
                />
              </div>
            </div>
          </div>
        ))}

        <button type="button" className="add-button" onClick={handleAddStep}>
          + Ajouter une étape
        </button>
      </div>
    </div>
  );
}

export default AddRecipe;
